import { Check, Download } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { Reveal, Marquee } from "@/components/motion";
import { DOCS } from "@/lib/site";
import { useLang } from "@/context/LangContext";

const VALUES = ["Trusted Company", "Global Access", "Global Hub / International Hub", "Sustainability Digital Ecosystem", "Strong Government Support", "One Stop Services"];
const NATIVE = [["native-1", "Cyrtosperma macrotum", "Papua"], ["native-2", "Homalomena sp. Red Sumatera", "Sumatera"], ["native-3", "Alocasia Tandurusa", "Sulawesi"], ["native-4", "Homalomena sp. Borneo", "Kalimantan"], ["native-5", "Leea Amabilis", "Kalimantan"]];
const MARKET = [["Florida – USA", "2 Juta tanaman", "2 million plants"], ["Jerman", "2 Juta tanaman", "2 million plants", "Germany"], ["Canada", "1,2 Juta tanaman", "1.2 million plants"], ["Korea Selatan", "1 Juta tanaman", "1 million plants", "South Korea"], ["Seattle – USA", "1 Juta tanaman", "1 million plants"], ["Cyprus", "500 Ribu tanaman", "500 thousand plants"], ["United Kingdom", "362 Ribu tanaman", "362 thousand plants"]];
const GROUP = [["Minaqu Indonesia", "Global Ornamental Plants Supplier", "native-3"], ["Minaqu Nusantara", "Green Landscaping & Interior Design Specialist", "land-3"], ["Minaqu Livestock", "Supplier & Production — peternakan bebek intensif, 5.000 ekor", "livestock", "Supplier & Production — intensive duck farming, 5,000 ducks"], ["Minaqu Global Trade", "Global Agriculture Resources & Trading Services", "pack-1"]];
const GOV = [["gov-1", "Ministry of State Owned Enterprises of the Republic of Indonesia"], ["gov-2", "Ministry of Cooperatives and SMEs of the Republic of Indonesia"], ["gov-3", "Ministry of Agriculture of the Republic of Indonesia"], ["gov-4", "9 Regional Partnership from Aceh to Papua"]];

const OWNER_POINTS = [
  ["Didirikan pada 10 November 2019 oleh Ade Wardhana Adinata S.E., M.M.", "Founded on 10 November 2019 by Ade Wardhana Adinata S.E., M.M."],
  ["Menjangkau pasar di lebih dari 15 negara di dunia.", "Reaching markets in more than 15 countries worldwide."],
  ["Opportunity: Indonesia adalah pusat asal & megabiodiversitas dengan 34 eco-region.", "Opportunity: Indonesia is a centre of origin & megabiodiversity with 34 eco-regions."],
  ["Superiority: tanaman yang dimiliki adalah tanaman asli Indonesia dengan beragam varietas.", "Superiority: our plants are native Indonesian plants in many varieties."],
  ["Minaqu Indonesia Platform — Global Integrated Agriculture Platform.", "Minaqu Indonesia Platform — a Global Integrated Agriculture Platform."],
];

const Owner = () => {
  const { t } = useLang();
  return (
  <section data-testid="about-owner" className="bg-white py-20 sm:py-28">
    <div className="mx-auto grid max-w-[1400px] items-center gap-12 px-5 sm:px-8 lg:grid-cols-12">
      <Reveal className="lg:col-span-5">
        <div className="relative mx-auto aspect-[4/5] max-w-md overflow-hidden arch bg-[var(--leaf)]">
          <img src="/assets/img/owner.webp" alt="Ade Wardhana Adinata S.E., M.M" className="absolute inset-x-0 bottom-0 mx-auto h-[95%] w-auto max-w-full object-contain object-bottom" />
        </div>
      </Reveal>
      <Reveal className="lg:col-span-7" delay={0.1}>
        <p className="eyebrow">Owner · Digital-Savvy Entrepreneur Hero</p>
        <h2 data-testid="about-owner-name" className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[0.95]">Ade Wardhana<br />Adinata <span className="text-[var(--leaf)]">S.E., M.M</span></h2>
        <ul className="mt-8 space-y-4 text-[var(--muted-ink)]">
          {OWNER_POINTS.map(([id, en]) => (
            <li key={id} className="flex gap-3"><span className="mt-1 grid h-5 w-5 shrink-0 place-items-center rounded-full bg-[var(--leaf)] text-white"><Check className="h-3 w-3" /></span>{t(id, en)}</li>
          ))}
        </ul>
      </Reveal>
    </div>
  </section>
  );
};

const Values = () => (
  <section data-testid="about-values" className="bg-[var(--leaf)] py-20 sm:py-28 text-white">
    <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
      <Reveal><p className="eyebrow !text-[#cdeed7]">Our Value</p></Reveal>
      <div className="mt-8 grid border-t border-white/25 sm:grid-cols-2 lg:grid-cols-3">
        {VALUES.map((v, i) => (
          <Reveal key={v} delay={i * 0.05} className="group border-b border-white/25 py-8 sm:pr-8">
            <p className="font-display text-xs font-bold text-white/70">0{i + 1}</p>
            <p className="mt-3 font-display text-2xl sm:text-3xl font-black tracking-tight transition-transform duration-500 group-hover:translate-x-2">{v}</p>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Market = () => {
  const { t } = useLang();
  return (
  <section data-testid="about-market" className="bg-[var(--paper)] py-20 sm:py-28">
    <div className="mx-auto grid max-w-[1400px] gap-12 px-5 sm:px-8 lg:grid-cols-2">
      <Reveal>
        <p className="eyebrow">Current Market Value</p>
        <h2 className="mt-4 font-display text-4xl sm:text-5xl font-black tracking-tight leading-[0.95]">{t("Kontrak bisnis & LOI di seluruh dunia.", "Business contracts & LOIs worldwide.")}</h2>
        <div className="mt-10 grid grid-cols-3 gap-4">
          {[["$230 bn", t("Potensi nilai pasar global (AIPH 2019–2021)", "Potential global market value (AIPH 2019–2021)")], ["60+", t("Negara — market retention", "Countries — market retention")], ["20+", t("Negara — client & existing market", "Countries — client & existing market")]].map(([v, l]) => (
            <div key={v} className="rounded-2xl bg-white p-4 ring-1 ring-leaf/10"><p className="font-display text-2xl sm:text-3xl font-black text-[var(--leaf)]">{v}</p><p className="mt-2 text-xs text-[var(--muted-ink)]">{l}</p></div>
          ))}
        </div>
      </Reveal>
      <Reveal delay={0.1}>
        <ul className="divide-y divide-leaf/15 border-y border-leaf/15">
          {MARKET.map(([c, v, ve, ce]) => <li key={c} className="flex items-baseline justify-between py-4"><span className="font-display text-lg font-extrabold">{t(c, ce || c)}</span><span className="text-sm text-[var(--leaf)] font-semibold">{t(v, ve)}</span></li>)}
        </ul>
        <p className="mt-5 text-sm text-[var(--muted-ink)]">Existing customer: Germany, Netherland, United Kingdom, Belgium, Poland, Norway, France, Cyprus, Canada, USA, South Korea, China, Malaysia, Singapore, Thailand, Australia, Nigeria.</p>
      </Reveal>
    </div>
  </section>
  );
};

const Native = () => {
  const { t } = useLang();
  return (
  <section data-testid="about-native" className="bg-white py-20 sm:py-28">
    <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
      <Reveal className="grid gap-6 lg:grid-cols-2 lg:items-end">
        <div><p className="eyebrow">We Preserve our Plants</p><h2 className="mt-4 font-display text-4xl sm:text-5xl font-black tracking-tight leading-[0.95]">Indonesia Native<br />Exotic Plants.</h2></div>
        <p className="text-[var(--muted-ink)]">{t("Kami berkolaborasi dengan petani di setiap provinsi di Indonesia untuk melestarikan tanaman — program berkelanjutan untuk kesejahteraan petani dan membuka peluang bisnis global.", "We collaborate with farmers in every province of Indonesia to preserve plants — a sustainable programme for farmer welfare that opens global business opportunities.")}</p>
      </Reveal>
      <div className="mt-12 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
        {NATIVE.map(([k, n, r], i) => (
          <Reveal key={k} delay={i * 0.06} className="group">
            <div className="aspect-[3/4] overflow-hidden arch"><img src={`/assets/img/${k}.jpg`} alt={n} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" /></div>
            <p className="mt-3 font-display text-sm font-bold text-[var(--leaf-deep)]">{n}</p><p className="text-xs text-[var(--muted-ink)]">{r}</p>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
  );
};

const Partnership = () => {
  const { t } = useLang();
  return (
  <section data-testid="about-partnership" className="bg-[var(--leaf-deep)] py-20 sm:py-28 text-white">
    <div className="mx-auto grid max-w-[1400px] items-center gap-12 px-5 sm:px-8 lg:grid-cols-2">
      <Reveal className="grid grid-cols-2 gap-4">
        <img src="/assets/img/esperit.webp" alt="Penandatanganan kemitraan Esperit Plants" className="col-span-1 aspect-[3/4] w-full rounded-[2rem] object-cover" />
        <div className="grid gap-4"><img src="/assets/img/pack-2.jpg" alt="Packing ekspor" className="aspect-square w-full rounded-[2rem] object-cover" /><img src="/assets/img/pack-4.jpg" alt="Handling ekspor" className="aspect-square w-full rounded-[2rem] object-cover" /></div>
      </Reveal>
      <Reveal delay={0.1}>
        <p className="eyebrow !text-[#9be0b0]">Strategic business partnership</p>
        <p className="mt-4 font-display text-6xl sm:text-7xl font-black tracking-tight">{t("7 Juta", "7 Million")}</p>
        <p className="font-display text-2xl font-extrabold">{t("Plantlet per tahun untuk TC Plants", "Plantlets per year for TC Plants")}</p>
        <p className="mt-6 text-white/85 leading-relaxed">{t("Kemitraan strategis bersama Esperit Plants (Woutersweg 16, 's-Gravenzande, Belanda). Dokumen, packing, dan handling ditangani oleh spesialis Perishable Goods Export Services.", "Strategic partnership with Esperit Plants (Woutersweg 16, 's-Gravenzande, the Netherlands). Documents, packing and handling are managed by Perishable Goods Export Services specialists.")}</p>
      </Reveal>
    </div>
  </section>
  );
};

const Group = () => {
  const { t } = useLang();
  return (
  <section data-testid="about-group" className="bg-[var(--paper)] py-20 sm:py-28">
    <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
      <Reveal><p className="eyebrow">Bisnis Ekosistem · Group</p><h2 className="mt-4 font-display text-4xl sm:text-5xl font-black tracking-tight">Minaqu Indonesia Group</h2></Reveal>
      <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {GROUP.map(([title, d, k, de], i) => (
          <Reveal key={title} delay={i * 0.07} className="group overflow-hidden rounded-[2rem] bg-white ring-1 ring-leaf/10">
            <div className="aspect-[4/3] overflow-hidden"><img src={`/assets/img/${k}.jpg`} alt={title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" /></div>
            <div className="p-6"><p className="font-display text-xl font-black text-[var(--leaf)]">{title}</p><p className="mt-2 text-sm text-[var(--muted-ink)]">{t(d, de || d)}</p></div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
  );
};

const Gov = () => {
  const { t } = useLang();
  return (
  <section data-testid="about-government" className="bg-white py-20 sm:py-28">
    <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
      <Reveal><p className="eyebrow">Government Support & Collaboration</p></Reveal>
      <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {GOV.map(([k, label], i) => (
          <Reveal key={k} delay={i * 0.07}>
            <div className="aspect-[4/3] overflow-hidden rounded-[1.5rem]"><img src={`/assets/img/${k}.jpg`} alt={label} className="h-full w-full object-cover" /></div>
            <p className="mt-3 text-sm font-semibold text-[var(--ink)]">{label}</p>
          </Reveal>
        ))}
      </div>
      <Reveal className="mt-14"><a href={DOCS.about} target="_blank" rel="noreferrer" data-testid="about-download-pdf" className="inline-flex items-center gap-2 rounded-full bg-[var(--leaf)] px-6 py-3.5 font-display text-sm font-bold text-white"><Download className="h-4 w-4" /> {t("Unduh Company Profile (PDF)", "Download Company Profile (PDF)")}</a></Reveal>
    </div>
  </section>
  );
};

export default function About() {
  const { t } = useLang();
  return (
    <>
      <PageHero testid="about-hero" eyebrow="About Minaqu Indonesia" lines={["Global", "Ornamental Plant", "Supplier."]} img="/assets/img/hero-leaves.jpg" imgAlt={t("Daun tanaman hias Minaqu", "Minaqu ornamental foliage")}
        intro={t("Global Ornamental Plant Supplier and Community Development. Minaqu Indonesia menyelesaikan masalah lewat pengembangan ekosistem bisnis berkelanjutan untuk petani Indonesia.", "Global Ornamental Plant Supplier and Community Development. Minaqu Indonesia solves problems by developing a sustainable business ecosystem for Indonesian farmers.")} />
      <Owner />
      <div className="border-y border-leaf/15 bg-white py-6"><Marquee items={["Farmer access", "Global market access", "Technology adaptation", "Sustainable development"]} className="font-display text-3xl sm:text-5xl font-black text-[var(--leaf)]" /></div>
      <Values />
      <Market />
      <Native />
      <Partnership />
      <Group />
      <Gov />
    </>
  );
}
